import { useState } from 'react'
import { Plus, Edit, Trash2, Ticket, Copy, Check } from 'lucide-react'
import { formatVND } from '@/lib/formatters'

function formatNumVN(n: number): string {
  if (!n) return ''
  return n.toLocaleString('vi-VN')
}
function parseNumVN(str: string): number {
  const digits = str.replace(/[^\d]/g, '')
  return digits ? parseInt(digits, 10) : 0
}
import { useAdminVouchers, useCreateVoucher, useUpdateVoucher, useDeleteVoucher, type Voucher, type CreateVoucherInput } from '@/modules/vouchers-api'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useToast } from '../../components/Toast'

const EMPTY: CreateVoucherInput = {
  code: '', description: '', type: 'PERCENT', value: 10,
  minOrder: 0, maxDiscount: 0, usageLimit: 0, active: true,
}

export function VouchersListPage() {
  const { data: vouchers = [], isLoading } = useAdminVouchers()
  const createMut = useCreateVoucher()
  const updateMut = useUpdateVoucher()
  const deleteMut = useDeleteVoucher()
  const push = useToast((s) => s.push)

  const [form, setForm] = useState<{ open: boolean; editing: Voucher | null }>({ open: false, editing: null })
  const [v, setV] = useState<CreateVoucherInput>(EMPTY)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const openCreate = () => { setV(EMPTY); setForm({ open: true, editing: null }) }
  const openEdit = (vou: Voucher) => {
    setV({
      code: vou.code, description: vou.description, type: vou.type, value: vou.value,
      minOrder: vou.minOrder, maxDiscount: vou.maxDiscount, usageLimit: vou.usageLimit,
      active: vou.active, endAt: vou.endAt,
    })
    setForm({ open: true, editing: vou })
  }

  const handleSave = async () => {
    if (!v.code.trim()) { push('error', 'Vui lòng nhập mã'); return }
    try {
      if (form.editing) {
        await updateMut.mutateAsync({ id: form.editing.id, input: v })
        push('success', `Đã cập nhật mã "${v.code}"`)
      } else {
        await createMut.mutateAsync(v)
        push('success', `Đã tạo mã "${v.code}"`)
      }
      setForm({ open: false, editing: null })
    } catch (err: any) {
      push('error', err?.message ?? 'Lưu thất bại')
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await deleteMut.mutateAsync(deleteId)
      push('success', 'Đã xóa mã')
    } catch (err: any) {
      push('error', err?.message ?? 'Xóa thất bại')
    }
    setDeleteId(null)
  }

  const target = deleteId ? vouchers.find((x) => x.id === deleteId) : null

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Mã giảm giá (Voucher)</h1>
          <p className="text-sm text-muted mt-1">{vouchers.length} mã trong hệ thống</p>
        </div>
        <button
          onClick={openCreate}
          className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Thêm mã mới
        </button>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Mã</th>
                <th className="text-left px-4 py-3 font-medium">Mô tả</th>
                <th className="text-right px-4 py-3 font-medium">Giảm</th>
                <th className="text-right px-4 py-3 font-medium">Đơn tối thiểu</th>
                <th className="text-center px-4 py-3 font-medium">Đã dùng</th>
                <th className="text-center px-4 py-3 font-medium">Trạng thái</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-4 py-16 text-center text-muted">Đang tải...</td></tr>}
              {!isLoading && vouchers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-muted mb-3">
                      <Ticket className="w-8 h-8" />
                    </div>
                    <div className="font-semibold text-text">Chưa có mã giảm giá nào</div>
                    <div className="text-sm text-muted mt-1">Bấm "Thêm mã mới" để tạo</div>
                  </td>
                </tr>
              )}
              {vouchers.map((vou) => (
                <tr key={vou.id} className="border-t border-border hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(vou.code)
                        setCopiedCode(vou.code)
                        setTimeout(() => setCopiedCode(null), 1500)
                      }}
                      className="font-mono font-bold text-brand-700 inline-flex items-center gap-1.5 hover:bg-brand-50 px-2 py-1 rounded"
                    >
                      {vou.code}
                      {copiedCode === vou.code ? <Check className="w-3.5 h-3.5 text-accent-600" /> : <Copy className="w-3.5 h-3.5 opacity-60" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted max-w-xs line-clamp-1">{vou.description || '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold">
                    {vou.type === 'PERCENT' ? `${vou.value}%` : formatVND(vou.value)}
                    {vou.type === 'PERCENT' && vou.maxDiscount > 0 && (
                      <div className="text-xs text-muted font-normal">tối đa {formatVND(vou.maxDiscount)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-muted">{vou.minOrder > 0 ? formatVND(vou.minOrder) : '—'}</td>
                  <td className="px-4 py-3 text-center text-xs text-muted">
                    {vou.usedCount}{vou.usageLimit > 0 && ` / ${vou.usageLimit}`}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {vou.active
                      ? <span className="bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">Đang bật</span>
                      : <span className="bg-slate-100 text-muted text-xs font-semibold px-2.5 py-0.5 rounded-full">Tạm tắt</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(vou)} className="w-8 h-8 rounded-md hover:bg-brand-50 text-brand-600 flex items-center justify-center" aria-label="Sửa">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteId(vou.id)} className="w-8 h-8 rounded-md hover:bg-red-50 text-danger flex items-center justify-center" aria-label="Xóa">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {form.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div onClick={() => setForm({ open: false, editing: null })} className="absolute inset-0 bg-black/50" />
          <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="h-14 px-5 border-b border-border flex items-center justify-between sticky top-0 bg-surface">
              <h3 className="font-semibold text-text">{form.editing ? 'Sửa mã giảm giá' : 'Tạo mã mới'}</h3>
              <button onClick={() => setForm({ open: false, editing: null })} className="text-muted hover:text-text text-2xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-sm font-medium mb-1.5">Mã code <span className="text-danger">*</span></div>
                  <input value={v.code} onChange={(e) => setV({ ...v, code: e.target.value.toUpperCase() })} placeholder="XEDIEN2026" className="w-full h-11 rounded-md border border-border px-3.5 text-sm font-mono uppercase outline-none focus:border-brand-500" />
                </label>
                <label className="block">
                  <div className="text-sm font-medium mb-1.5">Loại</div>
                  <select value={v.type} onChange={(e) => setV({ ...v, type: e.target.value as any })} className="w-full h-11 rounded-md border border-border px-3 text-sm outline-none focus:border-brand-500">
                    <option value="PERCENT">Giảm theo %</option>
                    <option value="FIXED">Giảm số tiền cố định</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Mô tả hiển thị</div>
                <input value={v.description ?? ''} onChange={(e) => setV({ ...v, description: e.target.value })} placeholder="Giảm 10% cho đơn từ 10 triệu" className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-sm font-medium mb-1.5">Giá trị {v.type === 'PERCENT' ? '(%)' : '(VND)'} <span className="text-danger">*</span></div>
                  <input type="text" inputMode="numeric" value={v.type === "PERCENT" ? (v.value || "") : formatNumVN(v.value)} onChange={(e) => setV({ ...v, value: v.type === "PERCENT" ? Number(e.target.value.replace(/[^\d]/g, "")) : parseNumVN(e.target.value) })} placeholder={v.type === "PERCENT" ? "10" : "100.000"} className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
                </label>
                {v.type === 'PERCENT' && (
                  <label className="block">
                    <div className="text-sm font-medium mb-1.5">Giảm tối đa (VND, 0 = không giới hạn)</div>
                    <input type="text" inputMode="numeric" value={formatNumVN(v.maxDiscount ?? 0)} onChange={(e) => setV({ ...v, maxDiscount: parseNumVN(e.target.value) })} placeholder="500.000" className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
                  </label>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-sm font-medium mb-1.5">Đơn tối thiểu (VND)</div>
                  <input type="text" inputMode="numeric" value={formatNumVN(v.minOrder ?? 0)} onChange={(e) => setV({ ...v, minOrder: parseNumVN(e.target.value) })} placeholder="10.000.000" className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
                </label>
                <label className="block">
                  <div className="text-sm font-medium mb-1.5">Lượt dùng tối đa (0 = vô hạn)</div>
                  <input type="text" inputMode="numeric" value={formatNumVN(v.usageLimit ?? 0)} onChange={(e) => setV({ ...v, usageLimit: parseNumVN(e.target.value) })} placeholder="100" className="w-full h-11 rounded-md border border-border px-3.5 text-sm outline-none focus:border-brand-500" />
                </label>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={v.active ?? true} onChange={(e) => setV({ ...v, active: e.target.checked })} className="w-4 h-4 accent-brand-600" />
                <span className="text-sm">Kích hoạt mã (cho phép sử dụng)</span>
              </label>
            </div>
            <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-3 sticky bottom-0 bg-surface">
              <button onClick={() => setForm({ open: false, editing: null })} className="h-10 px-5 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm">Hủy</button>
              <button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending} className="h-10 px-5 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm">
                {createMut.isPending || updateMut.isPending ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Xóa mã giảm giá"
        message={`Xóa mã "${target?.code}"? Hành động không thể hoàn tác.`}
        confirmLabel="Xóa"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}
